import { useEffect, useMemo, useState } from 'react'
import { useCameraSettings, useLocationTree, useUpdateCameraSettings } from '../../api/hooks'
import { ApiError } from '../../api/client'
import type { CameraForwardingSettings } from '../../api/types'
import { categoryLabels, sortCategories } from '../../lib/eventCategories'
import { locationPaths } from '../../lib/locations'
import { useAuthStore } from '../../stores/auth'
import { Button, Card, Chip, EmptyState, Skeleton } from '../ui'
import { EventCategoryChecklist } from './EventCategoryChecklist'
import { LocationMultiPicker } from './LocationMultiPicker'

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

const microLabel = 'text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted'

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const set = new Set(b)
  return a.every((x) => set.has(x))
}

/** Does the draft (as it would be saved) still match the given server
 *  settings? Used to decide whether a background refetch may re-seed the
 *  draft, or whether the operator has unsaved edits to protect. */
function sameCoverage(
  draft: { enabled: boolean; categories: string[]; locations: string[] },
  settings: CameraForwardingSettings,
): boolean {
  return (
    draft.enabled === settings.enabled &&
    sameSet(draft.categories, settings.categories) &&
    sameSet(draft.locations, settings.locations)
  )
}

/**
 * Wisenet WAVE camera: which Ostrel events get bookmarked on its recording.
 * Receives events on/off, the categories (empty = inherit the system
 * default), and the coverage — the camera's placement (fixed; edit it on
 * the Placement card) plus any extra locations. The camera receives events
 * at each of those locations and at any location above them (an event on a
 * floor reaches a camera in one of its rooms; an event in a room never
 * reaches a camera placed on the floor). Writes are admin-only.
 */
export function CameraBookmarksCard({ deviceUid }: { deviceUid: string }) {
  const settings = useCameraSettings(deviceUid)
  return (
    <Card title="Event bookmarks">
      {settings.isPending ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-6" />
          <Skeleton className="h-16" />
          <Skeleton className="h-24" />
        </div>
      ) : settings.isError ? (
        <EmptyState title="Could not load bookmark settings" hint={errDetail(settings.error)} />
      ) : (
        // Mounted once the settings load; the form owns its draft from there.
        <CameraBookmarksForm deviceUid={deviceUid} settings={settings.data} />
      )}
    </Card>
  )
}

function CameraBookmarksForm({ deviceUid, settings }: { deviceUid: string; settings: CameraForwardingSettings }) {
  const isAdmin = useAuthStore((s) => s.customer?.role === 'admin')
  const update = useUpdateCameraSettings(deviceUid)
  const tree = useLocationTree()
  const paths = useMemo(() => locationPaths(tree.data ?? []), [tree.data])

  const [enabled, setEnabled] = useState(settings.enabled)
  const [inherit, setInherit] = useState(settings.categories.length === 0)
  const [categories, setCategories] = useState<string[]>(sortCategories(settings.categories))
  const [locations, setLocations] = useState<string[]>(settings.locations)
  // The server data the draft was last reconciled against — lets a
  // background refetch (e.g. after the system defaults changed, or another
  // operator's edit) re-seed the draft, but only when there's nothing
  // unsaved to lose.
  const [seed, setSeed] = useState(settings)

  useEffect(() => {
    if (settings === seed) return
    const dirty = !sameCoverage({ enabled, categories: inherit ? [] : categories, locations }, seed)
    if (!dirty) {
      setEnabled(settings.enabled)
      setInherit(settings.categories.length === 0)
      setCategories(sortCategories(settings.categories))
      setLocations(settings.locations)
    }
    setSeed(settings)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- draft vs. previous seed compared explicitly above
  }, [settings])

  const readOnly = !isAdmin
  const customEmpty = !inherit && categories.length === 0

  function chooseCategories() {
    setInherit(false)
    // Start the explicit list from what the camera gets today.
    if (categories.length === 0) setCategories(sortCategories(settings.inherited_categories))
  }

  function save() {
    update.mutate({ enabled, categories: inherit ? [] : categories, locations })
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
        Ostrel events at the locations this camera covers are bookmarked on its WAVE recording.
      </p>

      <div className="flex flex-col gap-1.5">
        <span className={microLabel}>Receives events</span>
        <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ostrel-ink">
          <input
            type="checkbox"
            aria-label="Receives events"
            checked={enabled}
            disabled={readOnly}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-3.5 w-3.5 accent-ostrel-gold"
          />
          {enabled ? 'On' : 'Off'}
        </label>
      </div>

      <div className="flex flex-col gap-2">
        <span className={microLabel}>Categories</span>
        <div className="flex flex-wrap gap-6">
          <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ostrel-ink">
            <input
              type="radio"
              name={`categories-${deviceUid}`}
              checked={inherit}
              disabled={readOnly}
              onChange={() => setInherit(true)}
              className="h-3.5 w-3.5 accent-ostrel-gold"
            />
            Inherit system default
          </label>
          <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ostrel-ink">
            <input
              type="radio"
              name={`categories-${deviceUid}`}
              checked={!inherit}
              disabled={readOnly}
              onChange={chooseCategories}
              className="h-3.5 w-3.5 accent-ostrel-gold"
            />
            Choose categories
          </label>
        </div>
        {inherit ? (
          <p className="text-xs text-ostrel-ink-muted">
            {`Inherited: ${categoryLabels(settings.inherited_categories).join(', ') || 'none'}`}
          </p>
        ) : (
          <>
            <EventCategoryChecklist value={categories} onChange={setCategories} disabled={readOnly} />
            {customEmpty && (
              <p className="text-[11px] text-ostrel-warn">
                Pick at least one category, or inherit the system default.
              </p>
            )}
          </>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <span className={microLabel}>Locations ({(settings.placement ? 1 : 0) + locations.length})</span>
        <p className="text-[11px] text-ostrel-ink-muted">
          The camera gets events from its placement and any extra locations, and from any location above them. An event on a floor reaches cameras in its rooms; an event in a room reaches only that room's cameras.
        </p>
        {settings.placement ? (
          <div className="flex items-center gap-2 rounded-sm border border-ostrel-line bg-ostrel-card px-2 py-1 text-xs text-ostrel-ink">
            <span className="min-w-0 flex-1 truncate">{paths.get(settings.placement) ?? settings.placement}</span>
            <Chip tone="info" label="placement" />
          </div>
        ) : (
          <p className="text-[11px] text-ostrel-ink-muted">
            Unplaced — only the extra locations below are covered. Set a placement on the Placement card.
          </p>
        )}
        <LocationMultiPicker
          value={locations}
          onChange={setLocations}
          label="Extra camera locations"
          disabled={readOnly}
          exclude={settings.placement ? [settings.placement] : []}
        />
      </div>

      {update.isError && <p className="text-xs text-ostrel-danger">{errDetail(update.error)}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="navy"
          size="sm"
          disabled={readOnly || update.isPending || customEmpty}
          title={readOnly ? 'Requires the admin role' : undefined}
          onClick={save}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
        {update.isSuccess && !update.isPending && <span className="text-[11px] text-ostrel-ok">Saved.</span>}
      </div>
    </div>
  )
}
